import { useEffect, useMemo, useState } from 'react';
import type { BlockTypeInfo, DashboardBlock } from '../../api/dashboards';
import { Button, Input } from '../ui';
import {
  aggregationsFor,
  filterOperatorsFor,
  isNumeric,
  isTemporal,
  toBlock,
  type Aggregation,
  type BlockDraft,
  type BlockTypeKey,
  type DraftContext,
  type Granularity,
  withBoxMeasures,
  BLOCK_SHAPES,
} from './blockDraft';
import './BlockForm.css';

const GRANULARITIES: Granularity[] = ['day', 'week', 'month', 'quarter', 'year'];

const FILTER_LABELS: Record<string, string> = {
  in: 'is one of',
  eq: 'is exactly',
  contains: 'contains',
  between: 'is between',
  gte: 'is at least',
  lte: 'is at most',
};

const AGGREGATION_LABELS: Record<string, string> = {
  sum: 'Total',
  avg: 'Average',
  min: 'Lowest',
  max: 'Highest',
  median: 'Median',
  stddev: 'Spread',
  count: 'Rows',
  count_distinct: 'Distinct values',
};

export interface BlockFormProps {
  types: BlockTypeInfo[];
  context: DraftContext;
  /** Blocks a filter control can drive: everything that asks a question. */
  targets?: { id: string; title: string }[];
  /** Editing an existing block, or adding a new one. */
  initial?: BlockDraft;
  onSubmit: (block: DashboardBlock) => void;
  onCancel?: () => void;
}

/** `1 column`, `2 columns`. "column(s)" reads like a form, not a sentence. */
function count(amount: number, noun: string): string {
  return `${amount} ${noun}${amount === 1 ? '' : 's'}`;
}

function emptyDraft(type: BlockTypeKey): BlockDraft {
  return {
    id: `block_${Math.random().toString(36).slice(2, 9)}`,
    type,
    title: '',
    groupBy: [],
    measures: [],
    granularity: 'month',
    bins: 20,
    limit: 100,
  };
}

/**
 * The Coffee Lab's one form: what to show, of what, grouped how.
 *
 * It offers only what the chosen type can take — a pie asks for one grouping, a
 * heatmap for two, a KPI for none — so an impossible block cannot be described
 * in the first place. The backend still validates everything it sends; this is
 * a convenience, never the authority.
 */
export function BlockForm({
  types,
  context,
  targets = [],
  initial,
  onSubmit,
  onCancel,
}: BlockFormProps) {
  const [draft, setDraft] = useState<BlockDraft>(initial ?? emptyDraft('bar'));

  useEffect(() => {
    if (initial) setDraft(initial);
  }, [initial]);

  const shape = BLOCK_SHAPES[draft.type];
  const info = types.find((entry) => entry.key === draft.type);

  const groupable = context.schema;
  const firstGroup = context.schema.find((column) => column.name === draft.groupBy[0]);

  const measurable = useMemo(
    () => context.schema.filter((column) => column.type !== 'boolean'),
    [context.schema],
  );

  const update = (changes: Partial<BlockDraft>) =>
    setDraft((current) => ({ ...current, ...changes }));

  const setType = (type: BlockTypeKey) => {
    const next = BLOCK_SHAPES[type];
    setDraft((current) => ({
      ...current,
      type,
      groupBy: current.groupBy.slice(0, next.groupBy[1]),
      measures: type === 'box' ? [] : current.measures.slice(0, next.measures[1]),
    }));
  };

  const setGroup = (index: number, column: string) => {
    const groupBy = [...draft.groupBy];
    if (column) groupBy[index] = column;
    else groupBy.splice(index, 1);
    update({ groupBy: groupBy.filter(Boolean) });
  };

  const setMeasure = (
    index: number,
    changes: Partial<{ column: string; aggregation: Aggregation }>,
  ) => {
    const measures = [...draft.measures];
    const current = measures[index] ?? { column: '', aggregation: 'sum' as Aggregation };
    measures[index] = { ...current, ...changes };
    update({ measures });
  };

  const removeMeasure = (index: number) => {
    update({ measures: draft.measures.filter((_, position) => position !== index) });
  };

  const missing = (): string => {
    if (!draft.title.trim()) return 'Give the block a title.';
    if (draft.type === 'filter')
      return draft.filterColumn ? '' : 'Choose the column this filter narrows.';
    if (draft.type === 'text')
      return draft.text?.trim() ? '' : 'Write something for the block to say.';
    if (draft.groupBy.length < shape.groupBy[0])
      return `Choose ${count(shape.groupBy[0], 'column')} to group by.`;
    if (draft.type === 'box') {
      if (!draft.groupBy.length) return 'Choose a column to group by.';
      return draft.boxColumn ? '' : 'Choose the number whose spread you want.';
    }
    if (draft.measures.length < shape.measures[0])
      return `Choose ${count(shape.measures[0], 'measure')}.`;
    if (draft.measures.some((measure) => !measure.column)) return 'Every measure needs a column.';
    return '';
  };

  const problem = missing();

  const submit = () => {
    if (problem) return;
    onSubmit(toBlock(withBoxMeasures(draft), context));
  };

  return (
    <div className="sc-blockform">
      <label className="sc-blockform__field">
        <span>Show</span>
        <select
          value={draft.type}
          onChange={(event) => setType(event.target.value as BlockTypeKey)}
        >
          {types.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>
      {info && <p className="sc-blockform__hint">{info.description}</p>}

      <Input
        label="Title"
        value={draft.title}
        onChange={(event) => update({ title: event.target.value })}
      />

      {draft.type === 'filter' ? (
        <>
          <label className="sc-blockform__field">
            <span>Narrows</span>
            <select
              value={draft.filterColumn ?? ''}
              onChange={(event) => {
                const column = context.schema.find((entry) => entry.name === event.target.value);
                update({
                  filterColumn: event.target.value,
                  filterOperator: filterOperatorsFor(column)[0],
                });
              }}
            >
              <option value="">Choose a column…</option>
              {context.schema.map((column) => (
                <option key={column.name} value={column.name}>
                  {column.source_name}
                </option>
              ))}
            </select>
          </label>

          <label className="sc-blockform__field">
            <span>How</span>
            <select
              value={draft.filterOperator ?? 'in'}
              onChange={(event) => update({ filterOperator: event.target.value })}
            >
              {filterOperatorsFor(
                context.schema.find((entry) => entry.name === draft.filterColumn),
              ).map((option) => (
                <option key={option} value={option}>
                  {FILTER_LABELS[option] ?? option}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="sc-blockform__group">
            <legend>Drives</legend>
            <p className="sc-blockform__hint">
              {draft.targets?.length
                ? 'Only the blocks ticked below.'
                : 'Every block on the dashboard.'}
            </p>
            {targets.map((target) => (
              <label key={target.id} className="sc-blockform__check">
                <input
                  type="checkbox"
                  checked={draft.targets?.includes(target.id) ?? false}
                  onChange={(event) =>
                    update({
                      targets: event.target.checked
                        ? [...(draft.targets ?? []), target.id]
                        : (draft.targets ?? []).filter((id) => id !== target.id),
                    })
                  }
                />
                {target.title}
              </label>
            ))}
          </fieldset>
        </>
      ) : draft.type === 'text' ? (
        <label className="sc-blockform__field">
          <span>Text</span>
          <textarea
            rows={4}
            value={draft.text ?? ''}
            onChange={(event) => update({ text: event.target.value })}
          />
        </label>
      ) : (
        <>
          {shape.groupBy[1] > 0 && (
            <fieldset className="sc-blockform__group">
              <legend>Group by</legend>
              {Array.from({
                length: Math.max(
                  shape.groupBy[0],
                  Math.min(draft.groupBy.length + 1, shape.groupBy[1]),
                ),
              }).map((_, index) => (
                <label key={index} className="sc-blockform__field">
                  <span className="sc-visually-hidden">Column {index + 1}</span>
                  <select
                    value={draft.groupBy[index] ?? ''}
                    onChange={(event) => setGroup(index, event.target.value)}
                  >
                    <option value="">Choose a column…</option>
                    {groupable.map((column) => (
                      <option key={column.name} value={column.name}>
                        {column.source_name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}

              {isTemporal(firstGroup) && (
                <label className="sc-blockform__field">
                  <span>By</span>
                  <select
                    value={draft.granularity ?? 'month'}
                    onChange={(event) => update({ granularity: event.target.value as Granularity })}
                  >
                    {GRANULARITIES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {draft.type === 'histogram' && isNumeric(firstGroup) && (
                <label className="sc-blockform__field">
                  <span>Buckets</span>
                  <select
                    value={String(draft.bins ?? 20)}
                    onChange={(event) => update({ bins: Number(event.target.value) })}
                  >
                    {[10, 20, 30, 50].map((option) => (
                      <option key={option} value={option}>
                        about {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </fieldset>
          )}

          {draft.type === 'box' ? (
            <label className="sc-blockform__field">
              <span>Spread of</span>
              <select
                value={draft.boxColumn ?? ''}
                onChange={(event) => update({ boxColumn: event.target.value })}
              >
                <option value="">Choose a number…</option>
                {measurable
                  .filter((column) => isNumeric(column))
                  .map((column) => (
                    <option key={column.name} value={column.name}>
                      {column.source_name}
                    </option>
                  ))}
              </select>
            </label>
          ) : (
            shape.measures[1] > 0 && (
              <fieldset className="sc-blockform__group">
                <legend>Measure</legend>
                {Array.from({
                  length: Math.max(
                    shape.measures[0],
                    Math.min(draft.measures.length + 1, shape.measures[1]),
                  ),
                }).map((_, index) => {
                  const measure = draft.measures[index];
                  const column = context.schema.find((entry) => entry.name === measure?.column);
                  return (
                    <div key={index} className="sc-blockform__measure">
                      <select
                        aria-label={`Measure ${index + 1} column`}
                        value={measure?.column ?? ''}
                        onChange={(event) => setMeasure(index, { column: event.target.value })}
                      >
                        <option value="">Choose a column…</option>
                        {measurable.map((entry) => (
                          <option key={entry.name} value={entry.name}>
                            {entry.source_name}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label={`Measure ${index + 1} aggregation`}
                        value={measure?.aggregation ?? 'sum'}
                        onChange={(event) =>
                          setMeasure(index, { aggregation: event.target.value as Aggregation })
                        }
                      >
                        {aggregationsFor(column).map((option) => (
                          <option key={option} value={option}>
                            {AGGREGATION_LABELS[option] ?? option}
                          </option>
                        ))}
                      </select>
                      {measure && draft.measures.length > shape.measures[0] && (
                        <Button variant="secondary" size="sm" onClick={() => removeMeasure(index)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  );
                })}
              </fieldset>
            )
          )}
        </>
      )}

      {problem && <p className="sc-blockform__problem">{problem}</p>}

      <div className="sc-blockform__actions">
        <Button onClick={submit} disabled={!!problem}>
          {initial ? 'Save block' : 'Add block'}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
